// ComponentList.jsx
import React, { useEffect, useState } from "react";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Api } from "@/api/api";
import { useLoading } from "@/components/global-loading";
import { Tooltip } from "antd";
import { showConfirm, showDanger, showSuccess } from "@/utils";
import styles from "./ComponentList.css";
import { FormattedMessage, injectIntl } from "react-intl";

const ComponentList = ({
    setAgentRole,
    setDeivice,
    setChatRecordModalShow,
    intl,
}) => {
    const { showLoading, hideLoading } = useLoading();
    const [listData, setListData] = useState([]);

    const getDataList = () => {
        showLoading();
        setListData([]);
        Api.agent.getAgentList(
            ({ data }) => {
                hideLoading();
                if (data?.data) {
                    const originalDevices = data.data.map((item) => ({
                        ...item,
                        agentId: item.id,
                    }));
                    setListData(originalDevices);
                }
            },
            (error) => {
                hideLoading();
                console.error("Failed to fetch agent list:", error);
            }
        );
    };

    const formattedLastConnectedTime = (device) => {
        if (!device.lastConnectedAt) return intl.formatMessage({
            id: "gui.agentManage.noConversation",
            defaultMessage: "No conversation yet"
        });

        const lastTime = new Date(device.lastConnectedAt);
        const now = new Date();
        const diffMinutes = Math.floor((now - lastTime) / (1000 * 60));

        if (diffMinutes <= 1) {
            return intl.formatMessage({
                id: "gui.agentManage.justNow",
                defaultMessage: "Just now"
            });
        } else if (diffMinutes < 60) {
            return intl.formatMessage(
                { id: "gui.agentManage.minutesAgo", defaultMessage: "{minutes} minutes ago" },
                { minutes: diffMinutes }
            );
        } else if (diffMinutes < 24 * 60) {
            const hours = Math.floor(diffMinutes / 60);
            const minutes = diffMinutes % 60;
            if (minutes > 0) {
                return intl.formatMessage(
                    { id: "gui.agentManage.hoursMinutesAgo", defaultMessage: "{hours} hours {minutes} minutes ago" },
                    { hours, minutes }
                );
            }
            return intl.formatMessage(
                { id: "gui.agentManage.hoursAgo", defaultMessage: "{hours} hours ago" },
                { hours }
            );
        } else {
            return device.lastConnectedAt;
        }
    };

    useEffect(() => {
        getDataList();
    }, []);

    const handleDel = async (item) => {
        const resp = await showConfirm(intl.formatMessage({
            id: "gui.agentManage.confirmDelete",
            defaultMessage: "Are you sure you want to delete this agent?"
        }));
        if (resp) {
            showLoading(intl.formatMessage({
                id: "gui.agentManage.deleting",
                defaultMessage: "Deleting..."
            }));
            Api.agent.deleteAgent(item.agentId, (res) => {
                hideLoading();
                if (res.data.code === 0) {
                    showSuccess(intl.formatMessage({
                        id: "gui.agentManage.deleteSuccess",
                        defaultMessage: "Deleted successfully!"
                    }));
                    getDataList(); // 刷新列表
                } else {
                    showDanger(intl.formatMessage({
                        id: "gui.agentManage.deleteFailed",
                        defaultMessage: "Delete failed!"
                    }));
                }
            });
        }
    };

    const updateDeviceInfo = (device_id, payload, callback) => {
        return Api.device.updateDeviceInfo(device_id, payload, ({ data }) => {
            callback(data.code === 0, data);
        });
    };

    const handleOtaSwitchChange = async (row) => {
        updateDeviceInfo(
            row.device_id,
            { autoUpdate: row.otaSwitch ? 1 : 0 },
            (result, { msg }) => {
                if (result) {
                    showSuccess(
                        row.otaSwitch 
                            ? intl.formatMessage({ id: "gui.agentManage.autoUpdateEnabled", defaultMessage: "Auto update enabled" })
                            : intl.formatMessage({ id: "gui.agentManage.autoUpdateDisabled", defaultMessage: "Auto update disabled" })
                    );
                    return;
                }
                row.otaSwitch = !row.otaSwitch;
                showDanger(msg || intl.formatMessage({ id: "gui.agentManage.operationFailed", defaultMessage: "Operation failed" }));
            }
        );
    };

    return (
        <div className={styles.container}>
            {listData.map((item) => (
                <div key={item.id} className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.cardTitle}>{item.agentName}</div>
                        <div className={styles.headerIcons}>
                            <button
                                onClick={() => handleDel(item)}
                                className={`${styles.iconButton} ${styles.editIcon}`}
                            >
                                <DeleteOutlined />
                            </button>
                            <Tooltip
                                placement="topRight"
                                styles={{
                                    body: {
                                        width: 320,
                                    },
                                }}
                                title={item.systemPrompt}
                            >
                                <button
                                    className={`${styles.iconButton} ${styles.warningIcon}`}
                                >
                                    <ExclamationCircleOutlined />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>
                            <FormattedMessage id="gui.agentManage.llmModel" defaultMessage="LLM Model:" />
                        </span>
                        <span className={styles.infoValue}>
                            {item.llmModelName}
                        </span>
                    </div>

                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>
                            <FormattedMessage id="gui.agentManage.ttsModel" defaultMessage="TTS Model:" />
                        </span>
                        <span className={styles.infoValue}>
                            {item.ttsModelName} {item.ttsVoiceName}
                        </span>
                    </div>

                    <div className={styles.tagsContainer}>
                        <div className={styles.tagRow}>
                            <span
                                onClick={() =>
                                    setAgentRole({
                                        id: item.agentId,
                                        name: item.agentName,
                                    })
                                }
                                className={styles.customTag}
                            >
                                <FormattedMessage id="gui.agentManage.configRole" defaultMessage="Config Role" />
                            </span>
                            <span
                                onClick={() =>
                                    setDeivice({
                                        id: item.agentId,
                                        name: item.agentName,
                                    })
                                }
                                className={styles.customTag}
                            >
                                <FormattedMessage 
                                    id="gui.agentManage.deviceManagement" 
                                    defaultMessage="Device Management({count})" 
                                    values={{ count: item.deviceCount }}
                                />
                            </span>
                            <span
                                onClick={() =>
                                    setChatRecordModalShow({
                                        id: item.agentId,
                                        name: item.agentName,
                                    })
                                }
                                className={`${styles.customTag} ${
                                    item.memModelId === "Memory_nomem"
                                        ? styles.disabled
                                        : ""
                                }`}
                            >
                                <Tooltip
                                    title={
                                        item.memModelId === "Memory_nomem"
                                            ? intl.formatMessage({ 
                                                id: "gui.agentManage.enableMemoryFirst", 
                                                defaultMessage: "Please enable memory in 'Config Role' first" 
                                            })
                                            : ""
                                    }
                                >
                                    <FormattedMessage id="gui.agentManage.chatHistory" defaultMessage="Chat History" />
                                </Tooltip>
                            </span>
                        </div>
                    </div>

                    <div className={styles.statusRow}>
                        <span
                            style={{ color: "#999", fontSize: "12px" }}
                            className={styles.infoLabel}
                        >
                            <FormattedMessage 
                                id="gui.agentManage.recentConversation" 
                                defaultMessage="Recent conversation: {time}" 
                                values={{ time: formattedLastConnectedTime(item) }}
                            />
                        </span>
                        <span
                            onClick={() =>
                                setChatRecordModalShow({
                                    id: item.agentId,
                                    name: item.agentName,
                                })
                            }
                            className={styles.infoValue}
                        >
                            <FormattedMessage id="gui.agentManage.view" defaultMessage="View" />
                        </span>
                    </div>

                    <div className={styles.timeInfo}>
                        <span>
                            <FormattedMessage 
                                id="gui.agentManage.createTime" 
                                defaultMessage="Create Time: {time}" 
                                values={{ time: item.createTime || "-" }}
                            />
                        </span>
                        <span>
                            <FormattedMessage 
                                id="gui.agentManage.updateTime" 
                                defaultMessage="Update Time: {time}" 
                                values={{ time: item.updateTime || "-" }}
                            />
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default injectIntl(ComponentList);